import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Referral, ReferralTimeline } from '@/types/referral.types';
import { referralsService } from '@/services/referrals.service';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ReferralStatusBadge } from '@/components/referrals';

export const ReferralDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [referral, setReferral] = useState<Referral | null>(null);
  const [timeline, setTimeline] = useState<ReferralTimeline[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTimeline, setIsLoadingTimeline] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Rejection modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Determine if current user is sender or receiver
  const isSender = referral?.from_broker?.id === user?.id;
  const isReceiver = referral?.to_broker?.id === user?.id;

  const fetchReferral = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await referralsService.getById(id);
      // API interceptor unwraps { success: true, data: {...} } to just the data
      // Backend returns { referral: {...} } so extract the referral object
      const referralData = response.data?.referral || response.data;
      setReferral(referralData);
    } catch (err) {
      setError('Failed to load referral details');
      console.error('Error fetching referral:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchTimeline = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoadingTimeline(true);
      const response = await referralsService.getTimeline(id);
      // API interceptor unwraps { success: true, data: {...} } to just the data
      // Backend returns { timeline: [...] } so extract the timeline array
      const timelineData = response.data?.timeline || response.data || [];
      setTimeline(Array.isArray(timelineData) ? timelineData : []);
    } catch (err) {
      console.error('Error fetching timeline:', err);
    } finally {
      setIsLoadingTimeline(false);
    }
  }, [id]);

  useEffect(() => {
    fetchReferral();
    fetchTimeline();
  }, [fetchReferral, fetchTimeline]);

  const handleAccept = async () => {
    if (!id) return;

    try {
      setIsSubmitting(true);
      await referralsService.accept(id);
      fetchReferral();
      fetchTimeline();
    } catch (err) {
      console.error('Error accepting referral:', err);
      toast.error('Failed to accept referral');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!id || !rejectionReason.trim()) return;

    try {
      setIsSubmitting(true);
      await referralsService.reject(id, rejectionReason);
      setShowRejectModal(false);
      setRejectionReason('');
      fetchReferral();
      fetchTimeline();
    } catch (err) {
      console.error('Error rejecting referral:', err);
      toast.error('Failed to reject referral');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    if (!confirm('Are you sure you want to cancel this referral?')) return;

    try {
      setIsSubmitting(true);
      await referralsService.cancel(id);
      fetchReferral();
      fetchTimeline();
    } catch (err) {
      console.error('Error cancelling referral:', err);
      toast.error('Failed to cancel referral');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComplete = async () => {
    if (!id) return;
    if (!confirm('Are you sure you want to mark this referral as completed? This indicates the deal has closed.')) return;

    try {
      setIsSubmitting(true);
      await referralsService.complete(id);
      fetchReferral();
      fetchTimeline();
    } catch (err) {
      console.error('Error completing referral:', err);
      toast.error('Failed to complete referral');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-secondary-600">Loading referral details...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !referral) {
    return (
      <div className="text-center py-24">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">
          {error || 'Referral not found'}
        </h3>
        <p className="text-secondary-600 mb-4">
          We could not load the referral details.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" onClick={() => navigate('/referrals')}>
            Back to Referrals
          </Button>
          <Button onClick={fetchReferral}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate('/referrals')}
        className="inline-flex items-center text-sm text-secondary-600 hover:text-secondary-900"
      >
        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Referrals
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-secondary-900">
                Referral #{referral.referral_number}
              </h1>
              <ReferralStatusBadge status={referral.status} />
            </div>
            <p className="text-secondary-500">
              {isSender ? 'Sent to' : 'Received from'}{' '}
              <span className="text-secondary-700 font-medium">
                {isSender ? referral.to_broker.full_name : referral.from_broker.full_name}
              </span>
              {' on '}{formatDate(referral.created_at)}
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {referral.status === 'PENDING' && isReceiver && (
              <>
                <Button
                  onClick={handleAccept}
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  leftIcon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  }
                >
                  Accept
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setShowRejectModal(true)}
                  disabled={isSubmitting}
                  leftIcon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  }
                >
                  Reject
                </Button>
              </>
            )}
            {referral.status === 'PENDING' && isSender && (
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
                isLoading={isSubmitting}
              >
                Cancel Referral
              </Button>
            )}
            {referral.status === 'ACCEPTED' && (
              <Button
                onClick={handleComplete}
                disabled={isSubmitting}
                isLoading={isSubmitting}
                leftIcon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              >
                Mark Complete
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lead Information */}
          <Card>
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Lead Information</h3>
            <dl className="grid gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm text-secondary-500">Name</dt>
                <dd className="text-sm font-medium text-secondary-900 mt-1">{referral.lead.name}</dd>
              </div>
              <div>
                <dt className="text-sm text-secondary-500">Phone</dt>
                <dd className="text-sm font-medium text-secondary-900 mt-1">
                  <a
                    href={`tel:${referral.lead.phone}`}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    {referral.lead.phone}
                  </a>
                </dd>
              </div>
            </dl>
            <div className="flex gap-2 mt-4 pt-4 border-t border-secondary-200">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`tel:${referral.lead.phone}`, '_self')}
                leftIcon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                }
              >
                Call
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`https://wa.me/${referral.lead.phone.replace(/[^0-9]/g, '')}`, '_blank')}
                className="text-green-600 border-green-300 hover:bg-green-50"
                leftIcon={
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                }
              >
                WhatsApp
              </Button>
            </div>
          </Card>

          {/* Commission Split */}
          <Card>
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Commission Split</h3>
            <div className="space-y-4">
              {/* Visual Bar */}
              <div className="h-10 rounded-lg overflow-hidden flex">
                <div
                  className="bg-primary-500 flex items-center justify-center text-white text-sm font-medium transition-all duration-300"
                  style={{ width: `${referral.sender_split_percent}%` }}
                >
                  {referral.sender_split_percent > 15 && `${referral.sender_split_percent}%`}
                </div>
                <div
                  className="bg-emerald-500 flex items-center justify-center text-white text-sm font-medium transition-all duration-300"
                  style={{ width: `${referral.receiver_split_percent}%` }}
                >
                  {referral.receiver_split_percent > 15 && `${referral.receiver_split_percent}%`}
                </div>
              </div>

              {/* Labels */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-primary-50 rounded-lg">
                  <p className="text-sm text-primary-600 font-medium mb-1">
                    {referral.from_broker.full_name}
                  </p>
                  <p className="text-2xl font-bold text-primary-700">
                    {referral.sender_split_percent}%
                  </p>
                  <p className="text-xs text-primary-500 mt-1">Sender Split</p>
                </div>
                <div className="text-center p-4 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-600 font-medium mb-1">
                    {referral.to_broker.full_name}
                  </p>
                  <p className="text-2xl font-bold text-emerald-700">
                    {referral.receiver_split_percent}%
                  </p>
                  <p className="text-xs text-emerald-500 mt-1">Receiver Split</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Notes */}
          {referral.notes && (
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Notes</h3>
              <p className="text-sm text-secondary-700 whitespace-pre-wrap">{referral.notes}</p>
            </Card>
          )}

          {/* Rejection Reason */}
          {referral.status === 'REJECTED' && referral.rejection_reason && (
            <Card className="border-red-200 bg-red-50">
              <h3 className="text-lg font-semibold text-red-900 mb-2">Rejection Reason</h3>
              <p className="text-sm text-red-700 whitespace-pre-wrap">{referral.rejection_reason}</p>
            </Card>
          )}
        </div>

        {/* Right Column - Broker Info & Timeline */}
        <div className="space-y-6">
          {/* From Broker */}
          <Card>
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">From Broker</h3>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-semibold text-primary-600">
                  {referral.from_broker.full_name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-medium text-secondary-900">{referral.from_broker.full_name}</p>
                {referral.from_broker.firm_name && (
                  <p className="text-sm text-secondary-500">{referral.from_broker.firm_name}</p>
                )}
                {isSender && (
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                    You
                  </span>
                )}
              </div>
            </div>
          </Card>

          {/* To Broker */}
          <Card>
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">To Broker</h3>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-semibold text-emerald-600">
                  {referral.to_broker.full_name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-medium text-secondary-900">{referral.to_broker.full_name}</p>
                {referral.to_broker.firm_name && (
                  <p className="text-sm text-secondary-500">{referral.to_broker.firm_name}</p>
                )}
                {isReceiver && (
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                    You
                  </span>
                )}
              </div>
            </div>
          </Card>

          {/* Timeline */}
          <Card>
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Timeline</h3>
            {isLoadingTimeline ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
              </div>
            ) : timeline.length === 0 ? (
              <p className="text-sm text-secondary-500 text-center py-4">No timeline events</p>
            ) : (
              <div className="space-y-4">
                {timeline.map((event, index) => (
                  <div key={event.id} className="relative pl-6">
                    {/* Timeline connector */}
                    {index < timeline.length - 1 && (
                      <div className="absolute left-[7px] top-6 bottom-0 w-0.5 bg-secondary-200" />
                    )}
                    {/* Timeline dot */}
                    <div className="absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full bg-primary-500 border-2 border-white shadow-sm" />
                    {/* Content */}
                    <div>
                      <p className="text-sm font-medium text-secondary-900">{event.action}</p>
                      <p className="text-xs text-secondary-500 mt-0.5">{event.description}</p>
                      <p className="text-xs text-secondary-400 mt-1">{formatDate(event.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Key Dates */}
          <Card>
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Key Dates</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-secondary-500 uppercase tracking-wider">Created</dt>
                <dd className="text-sm font-medium text-secondary-900 mt-0.5">
                  {formatDate(referral.created_at)}
                </dd>
              </div>
              {referral.accepted_at && (
                <div>
                  <dt className="text-xs text-secondary-500 uppercase tracking-wider">Accepted</dt>
                  <dd className="text-sm font-medium text-green-600 mt-0.5">
                    {formatDate(referral.accepted_at)}
                  </dd>
                </div>
              )}
              {referral.completed_at && (
                <div>
                  <dt className="text-xs text-secondary-500 uppercase tracking-wider">Completed</dt>
                  <dd className="text-sm font-medium text-green-600 mt-0.5">
                    {formatDate(referral.completed_at)}
                  </dd>
                </div>
              )}
            </dl>
          </Card>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setShowRejectModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Reject Referral</h3>
            <p className="text-sm text-secondary-600 mb-4">
              Please provide a reason for rejecting this referral.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={3}
              className="w-full px-4 py-2.5 border border-secondary-300 rounded-lg text-secondary-900 placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => setShowRejectModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleReject}
                disabled={!rejectionReason.trim() || isSubmitting}
                isLoading={isSubmitting}
              >
                Reject Referral
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralDetail;
